import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { Dialog } from './Dialog'
import { BUTTON_CLASSES } from './Button'

// Promise-based in-app confirm/prompt — the branded, focus-managed replacement
// for window.confirm / window.prompt. `const { confirm, prompt } = useConfirm()`
// then `if (!(await confirm({ ... }))) return`. Confirmations need a blocking,
// focus-trapped choice (a toast can't gate a destructive action), so these go
// through the shared Dialog rather than the toast system.

export interface ConfirmOptions {
  title: string
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** 'warning' renders the confirm as the gold warning button (the action
   *  leaves the platform). 'danger' confirms use the primary button with a
   *  specific verb in `confirmLabel`. */
  tone?: 'default' | 'warning' | 'danger'
}

export interface PromptOptions {
  title: string
  label?: string
  defaultValue?: string
  placeholder?: string
  confirmLabel?: string
}

interface ConfirmApi {
  confirm: (opts: ConfirmOptions) => Promise<boolean>
  prompt: (opts: PromptOptions) => Promise<string | null>
}

const ConfirmCtx = createContext<ConfirmApi | null>(null)

export function useConfirm(): ConfirmApi {
  const ctx = useContext(ConfirmCtx)
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>')
  return ctx
}

type ConfirmState = { kind: 'confirm'; opts: ConfirmOptions }
type PromptState = { kind: 'prompt'; opts: PromptOptions }
type ActiveState = ConfirmState | PromptState

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveState | null>(null)
  const [promptValue, setPromptValue] = useState('')
  // The pending promise's resolver — settled exactly once per open dialog.
  const resolveRef = useRef<((value: unknown) => void) | null>(null)

  const settle = useCallback((value: boolean | string | null) => {
    const r = resolveRef.current
    resolveRef.current = null
    setActive(null)
    r?.(value)
  }, [])

  const confirm = useCallback((opts: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve as (v: unknown) => void
      setActive({ kind: 'confirm', opts })
    })
  }, [])

  const prompt = useCallback((opts: PromptOptions) => {
    return new Promise<string | null>((resolve) => {
      resolveRef.current = resolve as (v: unknown) => void
      setPromptValue(opts.defaultValue ?? '')
      setActive({ kind: 'prompt', opts })
    })
  }, [])

  const api = useMemo<ConfirmApi>(() => ({ confirm, prompt }), [confirm, prompt])

  return (
    <ConfirmCtx.Provider value={api}>
      {children}

      {active?.kind === 'confirm' ? (
        <Dialog
          open
          onClose={() => settle(false)}
          title={active.opts.title}
          footer={
            <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
              <button type="button" className={BUTTON_CLASSES.secondary} onClick={() => settle(false)}>
                {active.opts.cancelLabel ?? 'Cancel'}
              </button>
              <button
                type="button"
                className={active.opts.tone === 'warning' ? BUTTON_CLASSES.warning : BUTTON_CLASSES.primary}
                onClick={() => settle(true)}
              >
                {active.opts.confirmLabel ?? 'Confirm'}
              </button>
            </div>
          }
        >
          <div className="px-5 py-4 text-sm leading-relaxed text-ink-soft">
            {active.opts.body ?? null}
          </div>
        </Dialog>
      ) : null}

      {active?.kind === 'prompt' ? (
        <Dialog
          open
          onClose={() => settle(null)}
          title={active.opts.title}
          footer={
            <div className="flex justify-end gap-2 border-t border-line px-5 py-3">
              <button type="button" className={BUTTON_CLASSES.secondary} onClick={() => settle(null)}>
                Cancel
              </button>
              <button
                type="button"
                className={BUTTON_CLASSES.primary}
                onClick={() => settle(promptValue.trim() ? promptValue.trim() : null)}
              >
                {active.opts.confirmLabel ?? 'Save'}
              </button>
            </div>
          }
        >
          <form
            className="px-5 py-4"
            onSubmit={(e) => {
              e.preventDefault()
              settle(promptValue.trim() ? promptValue.trim() : null)
            }}
          >
            {active.opts.label ? (
              <label htmlFor="confirm-prompt-input" className="mb-1 block text-sm text-ink-soft">
                {active.opts.label}
              </label>
            ) : null}
            <input
              id="confirm-prompt-input"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              className="input"
              value={promptValue}
              placeholder={active.opts.placeholder}
              onChange={(e) => setPromptValue(e.target.value)}
            />
          </form>
        </Dialog>
      ) : null}
    </ConfirmCtx.Provider>
  )
}
